import { useRef, useState } from 'react'

import { Alert, Box, Button, TogglerCheckbox } from '@factorialco/deprecated-design-system'
import { RichTextEditorHandle } from '@factorialco/f0-react/dist/experimental'
import {
  AtsBulkCreateMessageDocument,
  AtsBulkCreateMessageMutation,
  ContactableApplicationFragment,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { DelayMessageSelector } from 'modules/ats/components/MessageBoard/NewMessage/DelayMessageSelector'
import { TextEditorWithMessageTemplates } from 'modules/ats/components/TextEditorWithMessageTemplates'
import { getDelayTime } from 'modules/ats/lib/utils/candidateEmailActions'

interface FormValues {
  content: string
  sendAsCorporateEmail: boolean
  messageDelayedUntil: string | null
}

interface Props {
  selectedApplications: ContactableApplicationFragment[]
  onClose: () => void
  onSuccess: () => void
}

export const SendEmailToApplicationsModal = ({
  selectedApplications,
  onClose,
  onSuccess,
}: Props) => {
  const [attachments, setAttachments] = useState<File[]>([])
  const richTextEditorRef = useRef<RichTextEditorHandle>(null)

  const {
    access: { userId },
  } = useCurrentSession()

  const [bulkCreateAtsMessage, { loading: submitting }] = useMutation(
    AtsBulkCreateMessageDocument,
    {
      extractorFn: (data: AtsBulkCreateMessageMutation) => data.atsMutations.bulkCreateAtsMessage,
    }
  )

  const getMessageDelay = (delay?: string | null): string | undefined => {
    if (!delay || delay === 'now') return undefined

    return getDelayTime(delay) ?? undefined
  }

  const form = useForm<FormValues>({
    defaultValues: {
      content: '',
      sendAsCorporateEmail: false,
      messageDelayedUntil: null,
    },
    validate(values) {
      const errors: Partial<Record<keyof FormValues, string>> = {}

      if (!values.content) {
        errors.content = i18n.t('common.validations.empty')
      }

      return errors
    },
    onSubmit: async (data) => {
      const delayedUntil = getMessageDelay(data.messageDelayedUntil)

      const response = await bulkCreateAtsMessage({
        variables: {
          atsApplicationIds: selectedApplications.map((application) => application.id),
          content: data.content,
          sendAsCorporateEmail: data.sendAsCorporateEmail,
          delayedUntil,
          attachments,
          sentById: userId,
          sentByType: 'user',
        },
      })

      const handled = handleResult(response, () => undefined)

      if (handled.success) {
        onSuccess()
        onClose()

        app.setFlash({
          type: 'correct',
          message: !delayedUntil
            ? i18n.tp('ats.candidates.applications.bulk_send_email.modal.sent_successfully', {
                count: selectedApplications.length,
              })
            : i18n.tp(
                'ats.candidates.applications.bulk_send_email.modal.delayed_sent_successfully',
                {
                  count: selectedApplications.length,
                }
              ),
        })
      }

      return handled
    },
  })

  const onFilesChange = (files: File[] | null | undefined) => {
    setAttachments(files ?? [])
  }

  const applicationsWithoutEmail = selectedApplications.filter(
    (application) => !application.candidate?.email
  ).length

  return (
    <Modal
      illustration="user-move"
      title={i18n.tp('ats.candidates.applications.bulk_send_email.modal.title', {
        count: selectedApplications.length,
      })}
      onClose={onClose}
      onSubmit={form.submitForm}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button disabled={submitting || !form.isValid} submit>
            {i18n.t('ats.candidates.applications.bulk_send_email.modal.send')}
          </Button>
        </>
      }
    >
      <Box gap="s24">
        <TextEditorWithMessageTemplates
          onChange={(value) => form.setFieldValue('content', value)}
          onFiles={onFilesChange}
          placeholder={i18n.t('ats.candidates.applications.bulk_send_email.modal.placeholder')}
          value={form.values.content}
          richTextEditorRef={richTextEditorRef}
        />
        <TogglerCheckbox
          value={form.values.sendAsCorporateEmail}
          onChange={(sendAsCorporateEmail) =>
            form.setFieldValue('sendAsCorporateEmail', sendAsCorporateEmail)
          }
          label={i18n.t('ats.candidates.applications.bulk_send_email.modal.send_as_corporate')}
          information={i18n.t(
            'ats.candidates.applications.bulk_send_email.modal.send_as_corporate_tooltip'
          )}
        />

        <DelayMessageSelector
          label={i18n.t('ats.smart_actions.sidepanel.delay')}
          value={form.values.messageDelayedUntil || ''}
          onChange={(messageDelayedUntil) =>
            form.setFieldValue('messageDelayedUntil', messageDelayedUntil)
          }
          inlineCustomPicker
        />

        {applicationsWithoutEmail > 0 ? (
          <Alert color="warning">
            {i18n.t(
              'ats.candidates.applications.bulk_send_email.modal.applications_without_email',
              {
                count: applicationsWithoutEmail,
              }
            )}
          </Alert>
        ) : null}
      </Box>
    </Modal>
  )
}
